import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Home',                  path: '/' },
  { label: 'How It Works',          path: '/how-it-works' },
  { label: 'Certificate Listings',  path: '/certificate-listings' },
  { label: 'Resources',             path: '/resources' },
  { label: 'Pricing',               path: '/pricing' },
  { label: 'Contact',               path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled]     = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location                    = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const solidBg = scrolled || mobileOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solidBg
          ? 'bg-[#090b10]/95 backdrop-blur-md border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">

        {/* Logo */}
        <Link to="/" className="flex items-center flex-shrink-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
            alt="Foreclosure Recovery"
            className="h-11 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-0.5">
          {NAV_LINKS.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap group ${
                  isActive(link.path) ? 'text-gold' : 'text-white/60 hover:text-white'
                }`}
              >
                {link.label}
                <span className={`absolute bottom-0 left-3 right-3 h-[1px] bg-gold transition-transform duration-300 origin-left ${
                  isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`} />
              </Link>
            </li>
          ))}
        </ul>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/login"
            className="text-sm font-medium text-white/55 hover:text-white transition-colors duration-200 whitespace-nowrap"
          >
            Member Login
          </Link>
          <Link
            to="/pricing"
            className="px-5 py-2.5 bg-gold text-navy-dark text-sm font-semibold rounded-lg hover:bg-gold-light transition-all duration-200 whitespace-nowrap animate-pulse-gold"
          >
            Join Now
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center text-white/70"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#090b10] border-t border-white/5">
          <ul className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`block px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'text-gold bg-white/5'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li className="pt-3 border-t border-white/8 mt-2">
              <Link
                to="/pricing"
                className="block w-full text-center px-5 py-3 bg-gold text-navy-dark text-sm font-semibold rounded-lg hover:bg-gold-light transition-colors whitespace-nowrap"
              >
                Join Now — View Plans
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
